<template>
    <div class="flex flex-col">
        <InnovationPost :item="item" :paginator-info="paginatorInfo" :index="index" :current-date-time="currentDateTime"
            @view-item="viewItem" @toggle-like="toggleLike" @open-comment="openComment" :is-modal="false" />
        <!-- Overlay -->
        <div v-if="showComments" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <!-- Modal -->
            <div
                class="bg-white w-full max-w-3xl h-80-vh sm:h-90-vh sm:rounded-lg shadow-lg border flex flex-col overflow-hidden">
                <!-- Header -->
                <div class="border-b-2 px-4 ht-60 flex items-center relative">
                    <div class="flex-grow flex justify-center text-xl">
                        <span class="font-semibold">{{ postTitle }}</span>
                    </div>
                    <div @click.stop="showComments = false"
                        class="absolute h-9 w-9 rounded-full bg-fb-gray right-4 flex flex-col justify-center items-center cursor-pointer">
                        <IconXMark class="h-6" />
                    </div>
                </div>

                <!-- Scrollable Content -->
                <div @scroll="handleScroll" class="flex-1 overflow-y-auto">
                    <InnovationPost :item="item" :paginator-info="paginatorInfo" :index="index"
                        :current-date-time="currentDateTime" @toggle-like="toggleLike" @open-comment="openComment"
                        :is-modal="true" />

                    <div v-if="!loadingComments && comments.length == 0"
                        class="flex  flex-col justify-center items-center pt-8">
                        <IconDocumentBlue />
                        <div class="mt-4 text-xl font-bold text-gray-500">No comments yet</div>
                        <div class="text-lg font-light text-gray-500">Be the first to comment.</div>
                    </div>
                    <div v-else class="comments-container">
                        <InnovationComment v-for="c in comments" :key="c.id" :comment="c"
                            :current-date-time="currentDateTime" :innovation-token="item.token"
                            @update-comments-count="updateCommentsCount" />
                        <div v-if="loadingComments" class="text-center py-2">Loading...</div>
                        <div v-if="allCommentsLoaded" class="text-center py-2 text-gray-400">No more comments</div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="border-t-2 flex p-4 gap-2">
                    <div class="flex-shrink-0 h-8 w-8">
                        <img :src="myProfilePicture" alt="User Avatar"
                            class="w-full h-full rounded-full object-cover border" />
                    </div>
                    <div class="flex-grow bg-gray-100 px-3 py-2 rounded-xl">
                        <textarea v-model="newComment" class="w-full bg-gray-100 resize-none outline-none text-sm"
                            :placeholder="`Comment as ${myName}`" @keyup.enter="addComment"></textarea>
                        <div class="flex justify-end bg-gray-100">
                            <div @click.stop="addComment"
                                class="h-7 w-7 flex flex-col justify-center items-center rounded-full"
                                :class="newComment ? 'cursor-pointer hover:bg-gray-300' : ''">
                                <IconPaperPlaneSolid class="h-4"
                                    :class="newComment ? 'text-blue-600' : 'text-gray-400 cursor-not-allowed'" />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import InnovationPost from "./InnovationPost.vue";
import InnovationComment from "./InnovationComment.vue";
import IconXMark from "../../../components/icons/IconXMark.vue";
import IconPaperPlaneSolid from "../../../components/icons/IconPaperPlaneSolid.vue";
import IconDocumentBlue from "../../../components/icons/IconDocumentBlue.vue";

const GET_THINK_TANK_COMMENTS = gql`
  query GetInnovationComments($innovation_token: String!, $page: Int!, $perPage: Int!) {
    innovationComments(innovation_token: $innovation_token, page: $page, perPage: $perPage) {
      data {
        id
        comment
        parent_id
        user {
          id
          name
          profile_picture_url
        }
        replies_count
        created_at
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
      }
    }
  }
`;


const ADD_THINK_TANK_COMMENT = gql`
  mutation AddInnovationComment($innovation_token: String!, $comment: String!) {
    addInnovationComment(innovation_token: $innovation_token, comment: $comment) {
        error
        error_code
        message
        data {
            id
            comment
            created_at
            user {
                id
                name
                profile_picture_url
            }
        }
    }
  }
`;

export default {
    name: "InnovationPostWithComment",
    props: ["item", "paginatorInfo", "index", "currentDateTime"],
    components: {
        InnovationPost,
        InnovationComment,
        IconXMark,
        IconPaperPlaneSolid,
        IconDocumentBlue,
    },
    data() {
        return {
            showComments: false,
            comments: [],
            page: 1,
            perPage: 10,
            loadingComments: false,
            allCommentsLoaded: false,
            newComment: '',
        };
    },
    computed: {
        postTitle() {
            return `${this.item.user.name}'s Post`;
        },
        myName() {
            return this.$store.getters.name;
        },
        myProfilePicture() {
            return this.$store.getters.profilePictureUrl;
        },
    },
    watch: {
        showComments(newVal) {
            if (newVal) {
                this.page = 1;
                this.comments = [];
                this.allCommentsLoaded = false;
                this.loadComments();
            }
        },
    },
    methods: {
        viewItem(item) {
            this.$emit('view-item', item);
        },
        toggleLike(token, index) {
            this.$emit('toggle-like', token, index);
        },
        openComment() {
            this.showComments = true;
        },

        async loadComments() {
            if (this.loadingComments || this.allCommentsLoaded) return;

            this.loadingComments = true;

            try {
                const { data } = await this.$apollo.query({
                    query: GET_THINK_TANK_COMMENTS,
                    variables: {
                        innovation_token: this.item.token,
                        page: this.page,
                        perPage: this.perPage,
                    },
                    fetchPolicy: 'network-only', // always fetch fresh data
                });

                if (data && data.innovationComments) {
                    const fetchedComments = data.innovationComments.data;
                    if (fetchedComments.length) {
                        this.comments = this.comments.concat(fetchedComments);
                        this.page += 1;
                    }

                    if (!data.innovationComments.paginatorInfo.hasMorePages) {
                        this.allCommentsLoaded = true;
                    }
                }
            } catch (err) {
                console.error('Failed to load comments:', err);
            } finally {
                this.loadingComments = false;
            }
        },



        handleScroll(event) {
            const el = event.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
                // user scrolled near bottom
                this.loadComments();
            }
        },


        async addComment() {
            if (!this.newComment.trim()) return;

            try {

                const { data } = await this.$apollo.mutate({
                    mutation: ADD_THINK_TANK_COMMENT,
                    variables: { innovation_token: this.item.token, comment: this.newComment },
                });

                const response = data.addInnovationComment;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                const responseData = response.data;

                const newComment = {
                    "id": responseData.id,
                    "comment": responseData.comment,
                    "parent_id": null,
                    "user": {
                        "__typename": "User",
                        "id": responseData.user.id,
                        "name": responseData.user.name,
                        "profile_picture_url": responseData.user.profile_picture_url
                    },
                    "created_at": responseData.created_at
                };

                this.comments.unshift(newComment);
                this.newComment = '';

                this.$emit('update-comments-count', this.item.token);

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not add Think Tank comment.',
                });
            }

        },
        updateCommentsCount(token) {
            this.$emit('update-comments-count', token);
        },
    },
}
</script>

<style lang="scss" scoped>
@responsive {
    .h-90-vh {
        height: 90vh;
    }

    .h-80-vh {
        height: 80vh;
    }
}

.ht-60 {
    height: 60px;
}
</style>